// Responsibility boundary:
// - owns local camera/local renderer lifecycle for the videocall feature
// - keeps the Voximplant camera-shutdown workaround isolated from the React UI
// - may reach into Voximplant private internals, but callers should not
import {Dispatch, MutableRefObject, SetStateAction} from 'react';
import * as VoxImplant from 'voximplant-websdk';
import {Call} from 'voximplant-websdk/Call/Call';
import {Logger} from '../../utils/managers/logger';

export type LocalRendererLike = {
  stream?: MediaStream | null;
  element?: HTMLMediaElement | null;
  clear?: () => void;
  isLocal?: boolean;
  kind?: string;
};

type InternalSharingEntry = {
  stream?: MediaStream | null;
  renderer?: {clear?: () => void} | null;
};

type InternalPeerConnectionLike = {
  _localStream?: MediaStream;
  getTransceivers?: () => Array<{sender?: {track?: MediaStreamTrack | null}}>;
  close?: () => void;
};

type InternalStreamManagerLike = {
  getLocalMediaRenderers?: () => LocalRendererLike[];
  _localMediaRenderers?: LocalRendererLike[];
  _dedicatedMirrorStream?: MediaStream | null;
  originalDedicatedTrack?: MediaStreamTrack | null;
  _callStreams?: Record<string, MediaStream | undefined>;
  _sharingStreams?: Record<string, InternalSharingEntry[] | undefined>;
  stopDedicatedMirrorStream?: () => void;
  remCallStream?: (call: Call) => void;
  clear?: () => void;
  isLocalVideoRequested?: boolean;
};

type LocalRendererRef = MutableRefObject<LocalRendererLike | null>;

type LocalVideoRefs = {
  localMediaRendererRef: LocalRendererRef;
  lastLocalMediaRendererRef: LocalRendererRef;
};

type LocalVideoStateControls = LocalVideoRefs & {
  setLocalVideoState: Dispatch<SetStateAction<number>>;
};

type ReleaseLocalVideoParams = LocalVideoStateControls & {
  call?: Call;
  notifyCall?: boolean;
  getCurrentCall: () => Call | undefined;
  logger: Logger;
};

type AggressiveReleaseLocalVideoParams = LocalVideoStateControls & {
  call?: Call;
  logger: Logger;
};

function clearMediaElement(mediaElement?: HTMLMediaElement | null) {
  if (!mediaElement) {
    return;
  }

  mediaElement.pause?.();

  if ('srcObject' in mediaElement) {
    mediaElement.srcObject = null;
  }

  mediaElement.removeAttribute('src');
  mediaElement.load?.();
}

function stopTracks(tracks: MediaStreamTrack[] = [], logger?: Logger) {
  tracks.forEach((track) => {
    try {
      track.stop();
    } catch (e) {
      logger?.debug('ERROR stopping media track', e);
    }
  });
}

function resetLocalVideoRefs({
  localMediaRendererRef,
  lastLocalMediaRendererRef,
  setLocalVideoState,
}: LocalVideoStateControls) {
  localMediaRendererRef.current = null;
  lastLocalMediaRendererRef.current = null;
  setLocalVideoState(1);
}

export function syncLocalMediaRenderer({
  localMediaRendererRef,
  lastLocalMediaRendererRef,
}: LocalVideoRefs) {
  const localVideoRenderer =
    (VoxImplant.Hardware.StreamManager.get().getLocalMediaRenderers() as LocalRendererLike[]).find(
      (renderer) => renderer.isLocal && renderer.kind === 'video'
    ) ?? null;

  localMediaRendererRef.current = localVideoRenderer;
  if (localVideoRenderer) {
    lastLocalMediaRendererRef.current = localVideoRenderer;
  }
  return localVideoRenderer;
}

export function aggressiveReleaseLocalVideo({
  call,
  logger,
  localMediaRendererRef,
  lastLocalMediaRendererRef,
  setLocalVideoState,
}: AggressiveReleaseLocalVideoParams) {
  const streamManager = VoxImplant.Hardware.StreamManager.get() as InternalStreamManagerLike;
  const peerConnection = call?.peerConnection as InternalPeerConnectionLike | undefined;

  const localRenderers = [
    localMediaRendererRef.current,
    lastLocalMediaRendererRef.current,
    ...(streamManager.getLocalMediaRenderers?.() ?? []),
    ...(streamManager._localMediaRenderers ?? []),
  ].filter(Boolean);

  localRenderers.forEach((renderer) => {
    stopTracks(renderer?.stream?.getTracks?.() ?? [], logger);
    try {
      renderer?.clear?.();
    } catch (e) {
      logger.debug('ERROR clearing media renderer', e);
    }
    clearMediaElement(renderer?.element);
    if (renderer && 'stream' in renderer) {
      renderer.stream = null;
    }
  });

  stopTracks(streamManager._dedicatedMirrorStream?.getTracks?.() ?? [], logger);
  stopTracks(
    streamManager.originalDedicatedTrack ? [streamManager.originalDedicatedTrack] : [],
    logger
  );
  stopTracks(peerConnection?._localStream?.getTracks?.() ?? [], logger);

  Object.values(streamManager._callStreams ?? {}).forEach((stream) => {
    stopTracks(stream?.getTracks?.() ?? [], logger);
  });

  Object.values(streamManager._sharingStreams ?? {}).forEach((entries) => {
    (entries ?? []).forEach((entry) => {
      stopTracks(entry?.stream?.getTracks?.() ?? [], logger);
      try {
        entry?.renderer?.clear?.();
      } catch (e) {
        logger.debug('ERROR clearing sharing renderer', e);
      }
    });
  });

  try {
    (peerConnection?.getTransceivers?.() ?? []).forEach((transceiver) => {
      const track = transceiver?.sender?.track;
      if (track?.readyState !== 'ended') {
        track?.stop?.();
      }
    });
  } catch (e) {
    logger.debug('ERROR stopping sender tracks', e);
  }

  try {
    streamManager.stopDedicatedMirrorStream?.();
  } catch (e) {
    logger.debug('ERROR stopping dedicated mirror stream', e);
  }

  if (call) {
    try {
      streamManager.remCallStream?.(call);
    } catch (e) {
      logger.debug('ERROR removing call stream during aggressive cleanup', e);
    }
  }

  try {
    peerConnection?.close?.();
  } catch (e) {
    logger.debug('ERROR closing peer connection during aggressive cleanup', e);
  }

  try {
    streamManager.clear?.();
  } catch (e) {
    logger.debug('ERROR clearing stream manager', e);
  }

  try {
    streamManager.isLocalVideoRequested = false;
    streamManager._localMediaRenderers = [];
    streamManager._sharingStreams = {};
    streamManager._dedicatedMirrorStream = null;
    streamManager.originalDedicatedTrack = null;
  } catch (e) {
    logger.debug('ERROR resetting stream manager internals', e);
  }

  resetLocalVideoRefs({
    localMediaRendererRef,
    lastLocalMediaRendererRef,
    setLocalVideoState,
  });
}

export async function releaseLocalVideo({
  call,
  notifyCall = true,
  getCurrentCall,
  logger,
  localMediaRendererRef,
  lastLocalMediaRendererRef,
  setLocalVideoState,
}: ReleaseLocalVideoParams) {
  const activeCall = call ?? getCurrentCall();
  const savedRenderer = localMediaRendererRef.current ?? lastLocalMediaRendererRef.current;
  const currentRenderer =
    syncLocalMediaRenderer({localMediaRendererRef, lastLocalMediaRendererRef}) ?? savedRenderer;
  const currentStream = currentRenderer?.stream;
  const currentElement = currentRenderer?.element;

  if (notifyCall && activeCall) {
    try {
      await activeCall.sendVideo(false);
    } catch (e) {
      logger.debug('ERROR Reject sendVideo(false) during cleanup', e);
    }
  }

  try {
    await VoxImplant.Hardware.StreamManager.get().hideLocalVideo();
  } catch (e) {
    logger.debug('ERROR Hiding local video during cleanup', e);
  }

  if (currentStream) {
    stopTracks((currentStream as MediaStream).getTracks(), logger);
  }

  clearMediaElement(currentElement);
  syncLocalMediaRenderer({localMediaRendererRef, lastLocalMediaRendererRef});
  resetLocalVideoRefs({
    localMediaRendererRef,
    lastLocalMediaRendererRef,
    setLocalVideoState,
  });
}
